import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';

import TableColumnHeader from '../TableColumnHeader';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { BaseColumnOptions, ColumnFactory } from './types';

/**
 * Options for boolean indicator columns
 */
export interface BooleanIndicatorColumnOptions extends BaseColumnOptions {
  /**
   * Enable sorting for this column
   */
  sortable?: boolean;
  /**
   * Tooltip to display next to the column header
   */
  infoTooltip?: string;
  /**
   * Icon to display when value is true
   */
  icon?: IconDefinition;
  /**
   * CSS class for the icon
   */
  iconClassName?: string;
  /**
   * Content to display when value is false
   */
  falseContent?: string;
  /**
   * Align content (default: 'center')
   */
  align?: 'left' | 'center' | 'right';
}

/**
 * Factory function to create a boolean indicator column
 */
export function createBooleanIndicatorColumn<TData>(config: {
  accessor: keyof TData & string;
  label: string;
  options?: BooleanIndicatorColumnOptions;
}): ColumnFactory<TData, boolean> {
  const { accessor, label, options = {} } = config;
  const {
    size = 150,
    sortable = false,
    className,
    infoTooltip,
    icon,
    iconClassName = '',
    falseContent = '',
    align = 'center',
    meta,
  } = options;

  const columnHelper = createColumnHelper<TData>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return columnHelper.accessor(accessor as any, {
    size,
    header: ({ table }) =>
      sortable ? (
        <TableColumnHeader
          className={className}
          accessor={accessor}
          infoTooltip={infoTooltip}
          label={label}
          setSorting={table.setSorting}
          sortable
          tableState={table.getState()}
        />
      ) : (
        <TableColumnHeader
          className={className}
          accessor={accessor}
          infoTooltip={infoTooltip}
          label={label}
          tableState={table.getState()}
        />
      ),
    cell: ({ getValue }) => {
      const value = getValue();
      if (value && icon) {
        return <FontAwesomeIcon className={`h-5 w-5 ${iconClassName}`.trim()} icon={icon} />;
      }
      return <span>{falseContent}</span>;
    },
    meta: {
      ...meta,
      style: {
        textAlign: align,
        ...meta?.style,
      },
    },
  }) as ColumnFactory<TData, boolean>;
}
